import { useState, useEffect } from "react";

//material ui imports
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Autocomplete,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

//project imports
import config from "../../../config";

//third party imports
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import useAxiosAuth from "../../../hooks/useAxiosAuth";

const JobPostForm = (props) => {
  const { onSave, onCancel, jobPost } = props;
  const theme = useTheme();
  const axiosAuth = useAxiosAuth();

  const [employers, setEmployers] = useState();
  const [loading, setLoading] = useState(false);

  const getEmployers = (skip) => {
    axiosAuth
      .get("/api/Employers/list?skip=" + skip + "&take=1000")
      .then((response) => {
        setEmployers(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    getEmployers(0);
  }, []);

  const formik = useFormik({
    initialValues: jobPost,
    validationSchema: Yup.object({
      title: Yup.string()
        .max(80, "Must be 80 characters or less")
        .required("Required"),
      jobDescription: Yup.string()
        // .max(2000, "Must be 2000 characters or less")
        .required("Required"),
      qualification: Yup.string()
        .max(255, "Must be 255 characters or less")
        .required("Required"),
      location: Yup.string()
        .max(255, "Must be 255 characters or less")
        .required("Required"),
      minSalary: Yup.number().min(0, "Must be greater than 0"),
      maxSalary: Yup.number().min(0, "Must be greater than 0"),
      noOfVaccancy: Yup.number()
        .min(1, "Must be greater than 1")
        .typeError("Must be a number"),
      minExprienceRequired: Yup.number().min(0, "Must be greater than 0"),
      maxExprienceRequired: Yup.number().min(0, "Must be greater than 0"),
      comments: Yup.string().max(500, "Must be 500 characters or less"),
    }),

    onSubmit: (values) => {
      if (jobPost.id != 0) {
        axiosAuth
          .put("/api/JobPosts", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      } else {
        axiosAuth
          .post("/api/JobPosts", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      }
    },
  });

  return (
    <>
      <>
        {jobPost.id === 0 ? (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <WorkOutlineIcon />
            <Typography
              color={theme.palette.primary.dark}
              variant="h5"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Add New Job Post
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <WorkOutlineIcon />
            <Typography
              color={theme.palette.primary.dark}
              variant="h5"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Edit Job Post Details
            </Typography>
          </Stack>
        )}
      </>
      <Divider sx={{ marginY: 2 }} />
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Stack>
              <FormControl sx={{ marginBottom: 2 }} fullWidth>
                {/* <InputLabel>Employer</InputLabel> */}
                <Autocomplete
                  fullWidth
                  options={employers?.employers || []}
                  getOptionLabel={(option) => option?.name || ""}
                  value={
                    employers?.employers?.find(
                      (emp) => emp.id === formik.values.employerId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "employerId",
                      newValue ? newValue.id : ""
                    );
                  }}
                  onBlur={formik.handleBlur}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employer"
                      margin="normal"
                      name="employerId"
                      error={
                        formik.touched.employerId &&
                        Boolean(formik.errors.employerId)
                      }
                      helperText={
                        formik.touched.employerId && formik.errors.employerId
                      }
                    />
                  )}
                />
              </FormControl>
              <FormControl sx={{ marginBottom: 2 }} fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select
                  name="jobType"
                  label="Job Type"
                  onChange={formik.handleChange}
                  value={formik.values.jobType}
                  error={
                    formik.touched.jobType && Boolean(formik.errors.jobType)
                  }
                  helperText={formik.touched.jobType && formik.errors.jobType}
                >
                  <MenuItem value={1}>Part Time</MenuItem>
                  <MenuItem value={2}>Full Time</MenuItem>
                  <MenuItem value={3}>Contract Basis</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="location"
                label="Location *"
                onChange={formik.handleChange}
                value={formik.values.location}
                fullWidth
                error={
                  formik.touched.location && Boolean(formik.errors.location)
                }
                helperText={formik.touched.location && formik.errors.location}
                sx={{ marginBottom: 2 }}
              />
              <TextField
                name="qualification"
                label="Qualifications Required"
                onChange={formik.handleChange}
                value={formik.values.qualification}
                fullWidth
                error={
                  formik.touched.qualification &&
                  Boolean(formik.errors.qualification)
                }
                helperText={
                  formik.touched.qualification && formik.errors.qualification
                }
                sx={{ marginBottom: 3 }}
              />
              <Typography sx={{ marginBottom: 2 }}>Salary / Month</Typography>
              <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                <TextField
                  name="minSalary"
                  type="number"
                  label="Min. *"
                  onChange={(e) => {
                    let value = e.target.value.replace(/^0+/, "");
                    formik.setFieldValue("minSalary", value);
                  }}
                  value={formik.values.minSalary}
                  fullWidth
                  error={
                    formik.touched.minSalary && Boolean(formik.errors.minSalary)
                  }
                  helperText={
                    formik.touched.minSalary && formik.errors.minSalary
                  }
                />
                <TextField
                  name="maxSalary"
                  type="number"
                  label="Max. *"
                  onChange={(e) => {
                    let value = e.target.value.replace(/^0+/, "");
                    formik.setFieldValue("maxSalary", value);
                  }}
                  value={formik.values.maxSalary}
                  fullWidth
                  error={
                    formik.touched.maxSalary && Boolean(formik.errors.maxSalary)
                  }
                  helperText={
                    formik.touched.maxSalary && formik.errors.maxSalary
                  }
                />
              </Stack>

              <Typography sx={{ marginBottom: 2 }}>
                Experience Required
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  name="minExprienceRequired"
                  type="number"
                  label="Min."
                  onChange={(e) => {
                    let value = e.target.value.replace(/^0+/, "");
                    formik.setFieldValue("minExprienceRequired", value);
                  }}
                  value={formik.values.minExprienceRequired}
                  fullWidth
                  error={
                    formik.touched.minExprienceRequired &&
                    Boolean(formik.errors.minExprienceRequired)
                  }
                  helperText={
                    formik.touched.minExprienceRequired &&
                    formik.errors.minExprienceRequired
                  }
                />
                <TextField
                  name="maxExprienceRequired"
                  type="number"
                  label="Max."
                  onChange={(e) => {
                    let value = e.target.value.replace(/^0+/, "");
                    formik.setFieldValue("maxExprienceRequired", value);
                  }}
                  value={formik.values.maxExprienceRequired}
                  fullWidth
                  error={
                    formik.touched.maxExprienceRequired &&
                    Boolean(formik.errors.maxExprienceRequired)
                  }
                  helperText={
                    formik.touched.maxExprienceRequired &&
                    formik.errors.maxExprienceRequired
                  }
                />
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={7} sx={{ mt: 2 }}>
            <TextField
              name="title"
              label="Title *"
              onChange={formik.handleChange}
              value={formik.values.title}
              fullWidth
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              sx={{ marginBottom: 3 }}
            />
            <TextField
              name="noOfVaccancy"
              label="Number Of Vacancy"
              type="number"
              onChange={formik.handleChange}
              value={formik.values.noOfVaccancy}
              fullWidth
              error={
                formik.touched.noOfVaccancy &&
                Boolean(formik.errors.noOfVaccancy)
              }
              helperText={
                formik.touched.noOfVaccancy && formik.errors.noOfVaccancy
              }
              sx={{ marginBottom: 2 }}
            />
            <Typography sx={{ marginBottom: 1 }}>Job Description</Typography>
            <CKEditor
              editor={ClassicEditor}
              data={jobPost.jobDescription}
              onChange={(event, editor) => {
                const data = editor.getData();
                formik.setFieldValue("jobDescription", data);
              }}
            />
            <Typography sx={{ margin: 1 }} variant="body2" color="error">
              {formik.errors.jobDescription}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Grid>
          <Button variant="contained" type="submit">
            Save
          </Button>
          <Button variant="outlined" onClick={onCancel} sx={{ marginLeft: 2 }}>
            Cancel
          </Button>
        </Grid>
      </form>
    </>
  );
};

export default JobPostForm;
